import * as Phaser from 'phaser';
import type { ToyVisualConfig } from '../config/toys';

export class ToySprite extends Phaser.GameObjects.Container {
  private floatTween?: Phaser.Tweens.Tween;
  private shadow!: Phaser.GameObjects.Graphics;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    private visual: ToyVisualConfig,
    private size = 80
  ) {
    super(scene, x, y);

    this.createShadow();
    this.createBody();
    this.createFace();
    this.createEmoji();
    this.createShine();

    this.addFloatAnimation();

    scene.add.existing(this);
  }

  private createBody(): void {
    const { color } = this.visual;
    const half = this.size / 2;

    const graphics = this.scene.add.graphics();
    graphics.fillStyle(parseInt(color.replace('#', '0x'), 16), 1);

    switch (this.visual.shape) {
      case 'square':
        graphics.fillRoundedRect(-half, -half, this.size, this.size, 20);
        break;
      case 'diamond':
        graphics.fillTriangle(0, -half, half, 0, 0, half);
        graphics.fillTriangle(0, -half, -half, 0, 0, half);
        break;
      case 'blob':
        this.drawBlob(graphics, 0, 0, half);
        break;
      case 'round':
      default:
        graphics.fillCircle(0, 0, half);
        break;
    }

    if (this.visual.ears) {
      graphics.fillCircle(-half * 0.45, -half * 0.85, half * 0.28);
      graphics.fillCircle(half * 0.45, -half * 0.85, half * 0.28);
    }

    this.add(graphics);
  }

  private createFace(): void {
    const { faceColor, accentColor } = this.visual;
    const half = this.size / 2;

    const face = this.scene.add.graphics();
    face.fillStyle(parseInt(faceColor.replace('#', '0x'), 16), 0.95);
    face.fillCircle(0, half * 0.15, half * 0.55);
    this.add(face);

    const eyeColor = parseInt(accentColor.replace('#', '0x'), 16);
    const eyes = this.scene.add.graphics();
    eyes.fillStyle(eyeColor, 1);
    eyes.fillCircle(-half * 0.22, half * 0.05, half * 0.1);
    eyes.fillCircle(half * 0.22, half * 0.05, half * 0.1);
    this.add(eyes);

    const mouth = this.scene.add.graphics();
    mouth.lineStyle(3, eyeColor, 1);
    mouth.beginPath();
    mouth.arc(0, half * 0.15, half * 0.15, 0.1 * Math.PI, 0.9 * Math.PI);
    mouth.strokePath();
    this.add(mouth);
  }

  private createEmoji(): void {
    const { emoji } = this.visual;
    const text = this.scene.add.text(0, -this.size * 0.35, emoji, {
      fontSize: `${this.size * 0.35}px`,
    }).setOrigin(0.5);
    this.add(text);
  }

  private createShine(): void {
    const half = this.size / 2;
    const shine = this.scene.add.graphics();
    shine.fillStyle(0xffffff, 0.35);
    shine.fillEllipse(-half * 0.35, -half * 0.35, half * 0.5, half * 0.3);
    this.add(shine);
  }

  private createShadow(): void {
    this.shadow = this.scene.add.graphics();
    this.shadow.fillStyle(0x000000, 0.12);
    this.shadow.fillEllipse(0, this.size * 0.55, this.size * 0.9, this.size * 0.22);
    this.addAt(this.shadow, 0);
  }

  private drawBlob(
    graphics: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    radius: number
  ): void {
    graphics.beginPath();
    const points = 8;
    for (let i = 0; i < points * 2; i++) {
      const angle = (i / (points * 2)) * Math.PI * 2;
      const r = i % 2 === 0 ? radius : radius * 0.75;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (i === 0) graphics.moveTo(x, y);
      else graphics.lineTo(x, y);
    }
    graphics.closePath();
    graphics.fillPath();
  }

  private addFloatAnimation(): void {
    this.floatTween = this.scene.tweens.add({
      targets: this,
      y: this.y - 10,
      duration: 1500 + Math.random() * 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: Math.random() * 1000,
    });
  }

  setGlow(color: number, intensity = 12): void {
    this.shadow.clear();
    this.shadow.fillStyle(color, 0.3);
    this.shadow.fillEllipse(0, this.size * 0.55, this.size * 0.9 + intensity, this.size * 0.22 + intensity);
  }

  setAlpha(alpha: number): this {
    super.setAlpha(alpha);
    return this;
  }

  destroy(fromScene?: boolean): void {
    this.floatTween?.stop();
    super.destroy(fromScene);
  }
}
